import React from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import { useQuery } from "@tanstack/react-query";
import { PaziaLoader, PaziaButton } from "components/atoms";
import { EmptyState, PaziaDeleteModal } from "components/molecules";
import { useToaster, toMoneyFormat, formatDate } from "utils";
import { courseTrainersApi } from "api/courseTrainersApi";
import {
  COURSE_TRAINER_COST_SCOPE,
  CourseTrainerCostEntryApi,
  CreateTrainerCostEntryRequest,
  UpdateTrainerCostEntryRequest,
  trainerCostsApi,
} from "api/trainer-costs/trainerCostsApi";
import { useCourseTrainerCostEntries } from "api/trainer-costs/useCourseTrainerCostEntries";
import { AddTrainerCostEntryInlineRow } from "./AddTrainerCostEntryInlineRow";
import { EditTrainerCostEntryModal } from "./EditTrainerCostEntryModal";
import { groupPricesBySession } from "./sessionGrouping";

interface TrainerCostEntriesTableProps {
  courseId: number;
}
export const TrainerCostEntriesTable = ({ courseId }: TrainerCostEntriesTableProps) => {
  const { showError, showSuccess } = useToaster();
  // Local delete state, not the shared DeleteResourceContext: this table sits next to other
  // AdminResourceDeleteModals (schedule list, course finances table), which would all open together.
  const [deleteEntryId, setDeleteEntryId] = React.useState<number | null>(null);
  const [isAdding, setIsAdding] = React.useState(false);
  const [editEntry, setEditEntry] = React.useState<CourseTrainerCostEntryApi | null>(null);
  const { isLoading, invalidate, costEntries } = useCourseTrainerCostEntries(courseId);
  const { data: courseTrainersData } = useQuery({
    queryKey: ["fetchTrainersByCourseId", courseId],
    queryFn: () => courseTrainersApi.fetchByCourseId(courseId),
  });

  const onCreate = async (data: CreateTrainerCostEntryRequest) => {
    try {
      await trainerCostsApi.createEntry(courseId, data);
      showSuccess("Dodano wpis kosztu");
      setIsAdding(false);
      invalidate();
    } catch (e) {
      showError("Wystąpił błąd podczas dodawania wpisu");
      console.error(e);
    }
  };

  const onEdit = async (data: UpdateTrainerCostEntryRequest) => {
    if (!editEntry) return;
    try {
      await trainerCostsApi.updateEntry(courseId, editEntry.id, data);
      showSuccess("Zaktualizowano wpis kosztu");
      setEditEntry(null);
      invalidate();
    } catch (e) {
      showError("Wystąpił błąd podczas edycji wpisu");
      console.error(e);
    }
  };

  const onDelete = async () => {
    if (deleteEntryId === null) return;
    try {
      await trainerCostsApi.deleteEntry(courseId, deleteEntryId);
      showSuccess("Usunięto wpis kosztu");
      invalidate();
    } catch (e) {
      showError("Wystąpił błąd podczas usuwania wpisu");
      console.error(e);
    } finally {
      setDeleteEntryId(null);
    }
  };

  const scopeLabel = (entry: CourseTrainerCostEntryApi) => {
    if (entry.scopeType === COURSE_TRAINER_COST_SCOPE.SESSION) {
      const trainer = courseTrainersData?.data.find((x) => x.id === entry.trainerId);
      const group = trainer
        ? groupPricesBySession(trainer.prices).find((g) => g.courseSessionId === entry.courseSessionId)
        : undefined;
      if (group) return group.label;
      return `Zjazd #${entry.courseSessionId}`;
    }
    if (entry.scopeType === COURSE_TRAINER_COST_SCOPE.DAY && entry.date)
      return `Dzień ${formatDate(entry.date, false, false, "short")}`;
    return "Cały kurs";
  };

  if (isLoading) return <PaziaLoader />;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Koszty trenerów</h3>
        {!isAdding && <PaziaButton title="Dodaj wpis" type="primary" onClick={() => setIsAdding(true)} />}
      </div>
      {isAdding && (
        <AddTrainerCostEntryInlineRow
          trainers={courseTrainersData?.data ?? []}
          onSubmit={onCreate}
          onCancel={() => setIsAdding(false)}
        />
      )}
      {costEntries.length === 0 && !isAdding ? (
        <EmptyState title="Brak wpisów" description="Dodaj pierwszy wpis kosztu trenera." centerDescription />
      ) : (
        costEntries.map((entry) => (
          <div
            key={entry.id}
            className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded bg-slate-100 p-2 text-sm dark:bg-slate-700 dark:text-slate-300"
          >
            <span className="min-w-[160px] flex-1">
              {entry.trainerFirstName} {entry.trainerSurname}
            </span>
            <span className="min-w-[180px] flex-1">{scopeLabel(entry)}</span>
            <span className="w-16 text-end">
              {entry.quantitySnapshot != null ? entry.quantitySnapshot.toFixed(2) : "—"}
            </span>
            <span className="min-w-[140px] text-end">
              {toMoneyFormat(entry.rateAmount)} PLN{entry.rateSourceName ? ` (${entry.rateSourceName})` : ""}
            </span>
            <span className="w-24 text-end font-semibold">{toMoneyFormat(entry.calculatedTotal)} PLN</span>
            <div className="flex gap-2">
              <MdEdit onClick={() => setEditEntry(entry)} size={20} className="cursor-pointer hover:text-blue-500" />
              <MdDelete
                size={20}
                onClick={() => setDeleteEntryId(entry.id)}
                className="cursor-pointer hover:text-red-500"
              />
            </div>
          </div>
        ))
      )}
      <EditTrainerCostEntryModal
        isOpen={!!editEntry}
        onCloseRequest={() => setEditEntry(null)}
        courseId={courseId}
        entry={editEntry}
        onSubmit={onEdit}
      />
      <PaziaDeleteModal
        id="delete-trainer-cost-entry-modal"
        title="Usuń wpis kosztu"
        description="Jesteś pewien że chcesz usunąć ten wpis kosztu trenera?"
        isOpen={deleteEntryId !== null}
        onCloseRequest={() => setDeleteEntryId(null)}
        onConfirm={onDelete}
      />
    </div>
  );
};
